"use client";

import { useEffect, useRef } from "react";
import posthog from "posthog-js";

const SCROLL_DEPTH_THRESHOLDS = [25, 50, 75, 100] as const;

function safe<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

export function getDistinctIdHeader(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const id = safe(() => posthog.get_distinct_id());
  return id ? { "x-posthog-distinct-id": id } : {};
}

interface IdentifyLeadOptions {
  phone?: string;
  email?: string;
  source: string;
  clinicName?: string;
  extra?: Record<string, unknown>;
}

export function identifyLead(opts: IdentifyLeadOptions) {
  if (typeof window === "undefined") return;
  const id = opts.phone || opts.email;
  if (!id) return;

  const now = new Date().toISOString();
  const setProps: Record<string, unknown> = {
    last_lead_source: opts.source,
    last_lead_at: now,
    ...opts.extra,
  };
  if (opts.phone) setProps.phone = opts.phone;
  if (opts.email) setProps.email = opts.email;
  if (opts.clinicName) setProps.clinic_name = opts.clinicName;

  safe(() =>
    posthog.identify(id, {
      $set: setProps,
      $set_once: {
        first_lead_source: opts.source,
        first_lead_at: now,
      },
    })
  );

  if (opts.clinicName) {
    safe(() =>
      posthog.group("clinic", opts.clinicName!, {
        name: opts.clinicName,
        first_seen_source: opts.source,
        ...(opts.extra ?? {}),
      })
    );
  }
}

export function track(event: string, properties?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  safe(() => posthog.capture(event, properties));
}

export function useFormTracking(formName: string, extraProps: Record<string, unknown> = {}) {
  const startedRef = useRef(false);
  const submittedRef = useRef(false);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "hidden" &&
        startedRef.current &&
        !submittedRef.current
      ) {
        track("form_abandoned", { form: formName, ...extraProps });
        submittedRef.current = true;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [formName, extraProps]);

  return {
    onFirstInteraction: () => {
      if (startedRef.current) return;
      startedRef.current = true;
      track("form_started", { form: formName, ...extraProps });
    },
    onSubmitted: () => {
      submittedRef.current = true;
    },
  };
}

export function useScrollDepth(pageName?: string) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const fired = new Set<number>();

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (docHeight <= 0) return;
      const percent = (scrollTop / docHeight) * 100;

      for (const threshold of SCROLL_DEPTH_THRESHOLDS) {
        if (percent >= threshold && !fired.has(threshold)) {
          fired.add(threshold);
          track("scroll_depth", {
            depth: threshold,
            page: pageName || window.location.pathname,
          });
        }
      }
    };

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pageName]);
}

export function usePageViewTracking(eventName: string, properties: Record<string, unknown>) {
  const sentRef = useRef(false);
  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    track(eventName, properties);
  }, [eventName, properties]);
}
